import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import styles from "./Projects.module.css";
import type { Project, ProjectStatus, SectionCopy } from "../content/types";
import { track } from "../lib/analytics";
import { OPEN_PROJECT_EVENT, projectIdFromHash } from "../lib/projectNav";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, ExternalLinkIcon } from "./icons";
import { OpenHausArchitecture } from "./OpenHausArchitecture";
import { TxSenderDiagram } from "./TxSenderDiagram";

function ProjectVisual({
  project,
  decorative,
  className,
}: {
  project: Project;
  decorative?: boolean;
  className?: string;
}) {
  if (project.diagram === "tx-sender") {
    return (
      <TxSenderDiagram
        className={className}
        decorative={decorative}
        title={project.imageAlt}
      />
    );
  }
  if (
    !decorative &&
    project.diagram === "openhaus" &&
    project.architecture
  ) {
    return (
      <OpenHausArchitecture
        architecture={project.architecture}
        decorative={decorative}
        className={className}
      />
    );
  }
  if (!project.imageSrc) return null;
  return (
    <img
      className={className}
      src={project.imageSrc}
      alt={decorative ? "" : (project.imageAlt ?? "")}
      width={decorative ? 640 : 960}
      height={decorative ? 400 : 600}
      loading={decorative ? "lazy" : undefined}
      decoding="async"
    />
  );
}

type ProjectsProps = {
  projects: Project[];
  section: SectionCopy;
};

type FilterId = "featured" | "live" | "deprecated" | "all";

const STATUS_LABEL: Record<ProjectStatus, string> = {
  live: "Live",
  deprecated: "Deprecated",
  sunset: "Sunset",
  internal: "Case study",
};

function isRetired(status: ProjectStatus) {
  return status === "deprecated" || status === "sunset";
}

function matchesFilter(project: Project, filter: FilterId) {
  if (filter === "all") return true;
  if (filter === "featured") return project.weight === "primary";
  if (filter === "live") return project.status === "live";
  return isRetired(project.status);
}

function stepIndex(current: number, delta: number, length: number) {
  if (length <= 0) return 0;
  return (current + delta + length) % length;
}

function clampIndex(index: number, length: number) {
  if (length <= 0) return 0;
  return Math.min(length - 1, Math.max(0, index));
}

export function Projects({ projects, section }: ProjectsProps) {
  const [filter, setFilter] = useState<FilterId>("featured");
  const [openId, setOpenId] = useState<string | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const featuredCount = projects.filter((p) => p.weight === "primary").length;
  const liveCount = projects.filter((p) => p.status === "live").length;
  const retiredCount = projects.filter((p) => isRetired(p.status)).length;

  const visible = useMemo(
    () => projects.filter((project) => matchesFilter(project, filter)),
    [projects, filter],
  );

  const openProjectById = useCallback(
    (id: string) => {
      const project = projects.find((item) => item.id === id);
      if (!project) return;
      setFilter((current) =>
        matchesFilter(project, current)
          ? current
          : project.weight === "primary"
            ? "featured"
            : "all",
      );
      setOpenId(id);
    },
    [projects],
  );

  const openIndex = visible.findIndex((project) => project.id === openId);
  const openProject = openIndex >= 0 ? visible[openIndex] : null;

  const updateTrackState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    setCanPrev(track.scrollLeft > 8);
    setCanNext(track.scrollLeft < max - 8);

    const cards = Array.from(track.querySelectorAll<HTMLElement>("[data-project-card]"));
    if (cards.length === 0) {
      setActiveIndex(0);
      return;
    }

    const trackLeft = track.getBoundingClientRect().left;
    let leading = 0;
    let leadingDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < leadingDistance) {
        leadingDistance = distance;
        leading = index;
      }
    });

    setActiveIndex(leading);
  }, []);

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = "smooth") => {
      const track = trackRef.current;
      const card = track?.querySelectorAll<HTMLElement>("[data-project-card]")[index];
      if (!track || !card) return;
      track.scrollTo({ left: card.offsetLeft, behavior });
    },
    [],
  );

  const scrollByCard = useCallback(
    (delta: number) => {
      const next = clampIndex(activeIndex + delta, visible.length);
      scrollToIndex(next);
    },
    [activeIndex, scrollToIndex, visible.length],
  );

  const stepProject = useCallback(
    (delta: number) => {
      if (visible.length === 0) return;
      const current = openIndex >= 0 ? openIndex : activeIndex;
      const next = stepIndex(current, delta, visible.length);
      const nextProject = visible[next];
      if (!nextProject) return;
      if (openId) {
        setOpenId(nextProject.id);
      }
      scrollToIndex(next);
    },
    [activeIndex, openId, openIndex, scrollToIndex, visible],
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateTrackState();
    track.scrollTo({ left: 0 });
    const frame = requestAnimationFrame(updateTrackState);
    return () => cancelAnimationFrame(frame);
  }, [filter, visible.length, updateTrackState]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => updateTrackState();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [updateTrackState]);

  useEffect(() => {
    if (openIndex < 0) return;
    scrollToIndex(openIndex);
  }, [openIndex, scrollToIndex]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (openProject) {
      if (!dialog.open) {
        if (typeof dialog.showModal === "function") {
          dialog.showModal();
        } else {
          dialog.setAttribute("open", "");
        }
      }
    } else if (dialog.open) {
      if (typeof dialog.close === "function") {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
      }
    }
  }, [openProject]);

  useEffect(() => {
    if (!openProject) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.scrollTo({ top: 0 });
    return () => {
      document.body.style.overflow = previous;
    };
  }, [openProject]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }

      const dir = event.key === "ArrowRight" ? 1 : -1;
      if (openId) {
        event.preventDefault();
        stepProject(dir);
        return;
      }

      const inProjects = Boolean(target?.closest?.("#projects"));
      if (!inProjects) return;
      event.preventDefault();
      scrollByCard(dir);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, scrollByCard, stepProject]);

  useEffect(() => {
    const applyHash = () => {
      const id = projectIdFromHash(window.location.hash);
      if (id) openProjectById(id);
    };
    const onOpen = (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (id) openProjectById(id);
    };

    applyHash();
    window.addEventListener("hashchange", applyHash);
    window.addEventListener(OPEN_PROJECT_EVENT, onOpen);
    return () => {
      window.removeEventListener("hashchange", applyHash);
      window.removeEventListener(OPEN_PROJECT_EVENT, onOpen);
    };
  }, [openProjectById]);

  const wasOpen = useRef(false);

  useEffect(() => {
    if (openId) {
      wasOpen.current = true;
      const hash = `#project-${openId}`;
      if (window.location.hash !== hash) {
        window.history.replaceState(null, "", hash);
      }
      return;
    }
    if (wasOpen.current && projectIdFromHash(window.location.hash)) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    }
    wasOpen.current = false;
  }, [openId]);

  const filters: Array<{ id: FilterId; label: string; count: number }> = [
    { id: "featured", label: "Flagship", count: featuredCount },
    { id: "live", label: "Live", count: liveCount },
    { id: "deprecated", label: "Deprecated", count: retiredCount },
    { id: "all", label: "All", count: projects.length },
  ];

  return (
    <section
      className="section section-alt"
      id="projects"
      aria-labelledby="projects-heading"
    >
      <div className="container">
        <div data-reveal>
          <p className="section-label">Selected projects</p>
          <h2 id="projects-heading">{section.heading}</h2>
          <p className="section-intro">{section.intro}</p>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.filters} role="group" aria-label="Filter projects">
            {filters.map((item) => {
              const selected = filter === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={selected}
                  className={`${styles.filter} ${selected ? styles.filterActive : ""}`}
                  onClick={() => {
                    setFilter(item.id);
                    setOpenId(null);
                  }}
                >
                  {item.label}
                  <span className={styles.filterCount}>{item.count}</span>
                </button>
              );
            })}
          </div>
          <p className={styles.hint}>
            {retiredCount} deprecated or sunset · {liveCount} still live
          </p>
        </div>

        <div className={styles.carousel}>
          <div className={styles.carouselHead}>
            <div className={styles.arrows}>
              <button
                type="button"
                className={styles.arrow}
                onClick={() => scrollByCard(-1)}
                disabled={!canPrev}
                aria-label="Previous projects"
              >
                <ChevronLeftIcon className={styles.arrowIcon} />
              </button>
              <button
                type="button"
                className={styles.arrow}
                onClick={() => scrollByCard(1)}
                disabled={!canNext}
                aria-label="Next projects"
              >
                <ChevronRightIcon className={styles.arrowIcon} />
              </button>
            </div>
          </div>

          <div
            ref={trackRef}
            className={styles.track}
            tabIndex={0}
            aria-label="Project cards"
          >
            {visible.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={() => setOpenId(project.id)}
              />
            ))}
          </div>
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={openProject ? titleId : undefined}
        onClose={() => setOpenId(null)}
        onClick={(event) => {
          if (event.target === event.currentTarget) setOpenId(null);
        }}
      >
        {openProject ? (
          <ProjectDetail
            project={openProject}
            titleId={titleId}
            indexLabel={`${openIndex + 1} / ${visible.length}`}
            onClose={() => setOpenId(null)}
            onPrev={() => stepProject(-1)}
            onNext={() => stepProject(1)}
            prevName={visible[stepIndex(openIndex, -1, visible.length)]?.name}
            nextName={visible[stepIndex(openIndex, 1, visible.length)]?.name}
          />
        ) : null}
      </dialog>
    </section>
  );
}

function ProjectCard({ project, onOpen }: { project: Project; onOpen: () => void }) {
  const retired = isRetired(project.status);

  return (
    <article
      className={`${styles.card} ${retired ? styles.cardRetired : ""}`}
      id={`project-${project.id}`}
      data-project-card
    >
      <button
        type="button"
        className={styles.cardButton}
        onClick={onOpen}
        aria-haspopup="dialog"
      >
        <figure
          className={`${styles.thumb} ${project.diagram === "tx-sender" ? styles.thumbDiagram : ""}`}
        >
          {project.diagram || project.imageSrc ? (
            <ProjectVisual project={project} decorative className={styles.media} />
          ) : (
            <div className={styles.thumbFallback} aria-hidden="true">
              <span>{project.name}</span>
            </div>
          )}
          <span className={`${styles.badge} ${styles[`badge_${project.status}`]}`}>
            {STATUS_LABEL[project.status]}
          </span>
        </figure>
        <div className={styles.cardBody}>
          {project.relatedCompany ? (
            <p className={styles.company}>{project.relatedCompany}</p>
          ) : null}
          <h3>{project.name}</h3>
          {project.role ? <p className={styles.role}>{project.role}</p> : null}
          <p className={styles.blurb}>{project.blurb}</p>
          {project.result ? <p className={styles.result}>{project.result}</p> : null}
          {project.statusNote ? (
            <p className={styles.note}>{project.statusNote}</p>
          ) : null}
          <span className={styles.openHint}>
            View case study
            <ChevronRightIcon className={styles.openIcon} />
          </span>
        </div>
      </button>
    </article>
  );
}

function ProjectDetail({
  project,
  titleId,
  indexLabel,
  onClose,
  onPrev,
  onNext,
  prevName,
  nextName,
}: {
  project: Project;
  titleId: string;
  indexLabel: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  prevName?: string;
  nextName?: string;
}) {
  return (
    <div className={styles.detail}>
      <div className={styles.detailBar}>
        <p className={styles.detailKicker}>
          {project.relatedCompany ? `${project.relatedCompany} · ` : ""}
          {STATUS_LABEL[project.status]}
          <span className={styles.detailIndex}>{indexLabel}</span>
        </p>
        <div className={styles.detailActions}>
          <button
            type="button"
            className={styles.arrow}
            onClick={onPrev}
            aria-label={prevName ? `Previous project: ${prevName}` : "Previous project"}
          >
            <ChevronLeftIcon className={styles.arrowIcon} />
          </button>
          <button
            type="button"
            className={styles.arrow}
            onClick={onNext}
            aria-label={nextName ? `Next project: ${nextName}` : "Next project"}
          >
            <ChevronRightIcon className={styles.arrowIcon} />
          </button>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close project"
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>
      </div>

      {project.diagram === "tx-sender" ||
      project.diagram === "openhaus" ||
      project.imageSrc ? (
        <figure
          className={`${styles.detailFigure} ${
            project.diagram === "tx-sender"
              ? styles.detailFigureDiagram
              : project.diagram === "openhaus"
                ? styles.detailFigureArchitecture
                : ""
          }`}
        >
          <ProjectVisual project={project} className={styles.media} />
        </figure>
      ) : null}

      <div className={styles.detailCopy}>
        <h3 id={titleId}>{project.name}</h3>
        {project.role ? <p className={styles.role}>{project.role}</p> : null}
        {project.result ? <p className={styles.result}>{project.result}</p> : null}
        <p className={styles.summary}>{project.summary}</p>
        {project.statusNote ? <p className={styles.note}>{project.statusNote}</p> : null}

        <dl className={styles.fields}>
          <CaseField title="Problem" body={project.problem} />
          <CaseField title="My role" body={project.contribution} />
          {project.team ? <CaseField title="Team" body={project.team} /> : null}
          <CaseField title="Constraints" items={project.constraints} />
          <CaseField title="Key decisions" items={project.decisions} />
          <CaseField title="Approach" items={project.approach} />
          <CaseField title="Execution" items={project.execution} />
          <CaseField title="Outcome" items={project.outcome} />
          <CaseField title="Evidence" items={project.evidence} />
          {project.extraSections?.map((section) => (
            <CaseField
              key={section.title}
              title={section.title}
              body={section.body}
              items={section.bullets}
            />
          ))}
          <CaseField title="What I would change" items={project.learned} />
        </dl>

        <div className="tag-row">
          {project.technologies.map((tech) => (
            <span className="tag" key={tech}>
              {tech}
            </span>
          ))}
        </div>

        {project.links.length > 0 ? (
          <ul className={styles.links}>
            {project.links.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("project_link_click", {
                      project: project.id,
                      label: link.label,
                    })
                  }
                >
                  {link.label}
                  <ExternalLinkIcon className={styles.linkIcon} />
                </a>
              </li>
            ))}
          </ul>
        ) : null}

        <div className={styles.detailNav}>
          <button type="button" className={styles.detailNavBtn} onClick={onPrev}>
            <ChevronLeftIcon className={styles.arrowIcon} />
            <span>{prevName ?? "Previous"}</span>
          </button>
          <button type="button" className={styles.detailNavBtn} onClick={onNext}>
            <span>{nextName ?? "Next"}</span>
            <ChevronRightIcon className={styles.arrowIcon} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CaseField({
  title,
  body,
  items,
}: {
  title: string;
  body?: string;
  items?: string[];
}) {
  const hasBody = Boolean(body?.trim());
  const list = items?.filter((item) => item.trim().length > 0) ?? [];
  if (!hasBody && list.length === 0) return null;

  return (
    <div>
      <dt>{title}</dt>
      <dd>
        {hasBody ? <p className={styles.fieldBody}>{body}</p> : null}
        {list.length > 0 ? (
          <ul>
            {list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
      </dd>
    </div>
  );
}
